import { HttpMethod } from '@bioliberty/http';
import { GeneratePatientAliasResponse, Patient, PatientPage } from '@bioliberty/lifemind';
import { tms } from './tms';

interface AddPatientParams {
  alias: string;
  firstName?: string;
  lastName?: string;
  tenantId?: string;
}

interface UpdatePatientParams {
  id: number;
  alias: string;
  firstName?: string;
  lastName?: string;
}

interface GetPatientsParams {
  page: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  searchTerm?: string;
  tenantIds?: string[];
}

interface GeneratePatientAliasParams {
  tenantId: string;
}

const patient = tms.injectEndpoints({
  endpoints: (builder) => ({
    addPatient: builder.mutation<Patient, AddPatientParams>({
      query: (newPatient) => ({
        url: `patients`,
        method: 'POST',
        body: {
          ...newPatient
        }
      }),
      invalidatesTags: ['Patient']
    }),
    updatePatient: builder.mutation<Patient, UpdatePatientParams>({
      query: (updatedPatient) => ({
        url: `patients/${updatedPatient.id}`,
        method: 'PUT',
        body: {
          ...updatedPatient
        }
      }),
      invalidatesTags: ['Patient']
    }),
    getPatient: builder.query<Patient, string>({
      query: (id) => ({
        url: `patients/${id}`,
        method: 'GET'
      }),
      transformResponse: (response: Patient) => ({
        ...response,
        createdAt: response.createdAt,
        lastActivityDate: response.lastActivityDate
      }),
      providesTags: (_result, _error, id) => [{ type: 'Patient', id: id }]
    }),
    deletePatient: builder.mutation<void, string | number>({
      query: (id) => ({
        url: `patients/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Patient']
    }),
    getPatients: builder.query<PatientPage, GetPatientsParams>({
      query: ({ page, pageSize, sortBy, sortOrder, searchTerm, tenantIds }) => ({
        url: `patients`,
        method: 'GET',
        params: {
          page,
          pageSize,
          sortBy,
          sortOrder,
          searchTerm,
          tenantIds: tenantIds?.join(',')
        }
      }),
      transformResponse: (response: PatientPage) => ({
        ...response,
        patients: response.patients.map((patient) => ({
          ...patient,
          createdAt: patient.createdAt,
          lastActivityDate: patient.lastActivityDate
        }))
      }),
      providesTags: ['Patient']
    }),
    generatePatientAlias: builder.query<GeneratePatientAliasResponse, GeneratePatientAliasParams>({
      query: ({ tenantId }) => ({
        url: `patients/alias/generate`,
        params: { tenantId },
        method: HttpMethod.Get,
        cache: 'no-cache'
      })
    })
  }),
  overrideExisting: false
});

export const { useMutation: useAddPatientMutation } = patient.endpoints.addPatient;
export const { useMutation: useUpdatePatientMutation } = patient.endpoints.updatePatient;
export const { useMutation: useDeletePatientMutation } = patient.endpoints.deletePatient;
export const { useLazyQuery: useGeneratePatientAliasLazyQuery } = patient.endpoints.generatePatientAlias;
export const { useQuery: useGetPatientQuery } = patient.endpoints.getPatient;
export const { useQuery: useGetPatientsQuery } = patient.endpoints.getPatients;
